import { defineStore } from "pinia";
import Echo from 'laravel-echo'

import Router from './../routes';
import Http from '../utils/http';
import Utils from '../utils/utils';
import Storage from '../utils/storage'
import i18n from '../localizations';

import { useDashboardStore } from "./dashboard";
import { useDocumentsStore } from "./documents";
import { useTemplatesStore } from "./templates";
import { useSituationsStore } from "./situations";
import { useUsersStore } from "./users";
import { useGamesStore } from "./games";
import { useGameStore } from "./game";

export const useRootStore = defineStore("root", {
    state: () => ({
        loading: false,
        authenticated: false,
        user: null,
        notifications: [],
        errors: {},
        websocket: null,
    }),
    
    getters:  {
        hasRole: (state) => (roleKey) => {
            if (state.user === null || !state.user.role || !state.user.role.key) {
                return false;
            }

            return state.user.role.key === roleKey;
        },
        
        isAdmin() {
            return this.hasRole("admin");
        },
        
        isLecturer() {
            return this.hasRole("lecturer");
        },
        
        isPlayer() {
            return this.hasRole("player");
        },
    },

    actions: {
        setLoading(loading) {
            this.loading = loading;
        },

        authenticate() {
            this.authenticated = !!Storage.getAccessToken();
        },

        setUser(user) {
            if (!user) {
                this.user = null;
                return;
            }

            this.user = {
                id: user['id'],
                firstName: user['first_name'],
                lastName: user['last_name'],
                fullName: user['full_name'],
                email: user['email'],
                avatar: user['avatar'],
                lecturerId: user['lecturer_id'],
                role: user['role']
            };
        },
        
        updateUserLecturer(newLecturerId) {
            const { lecturerId } = this.user;
            
            if (lecturerId) {
                this.websocket.leave(`Classroom.${lecturerId}`);
            }
            
            this.user.lecturerId = newLecturerId;
            this.websocket.join(`Classroom.${newLecturerId}`);
        },

        initWebSocket() {
            if (this.websocket) {
                this.websocket.disconnect();
            }

            this.websocket = new Echo({
                broadcaster: 'pusher',
                key: process.env.MIX_PUSHER_APP_KEY,
                wsHost: process.env.MIX_PUSHER_APP_HOST,
                cluster: process.env.MIX_PUSHER_APP_CLUSTER,
                wsPort: process.env.MIX_LARAVEL_WEBSOCKETS_PORT,
                wssPort: process.env.MIX_LARAVEL_WEBSOCKETS_PORT,
                encrypted: true,
                forceTLS: false,
                disableStats: true,
                authorizer: (channel, options) => {
                    return {
                        authorize: (socketId, callback) => {
                            Http.post('/broadcasting/auth', {
                                socket_id: socketId,
                                channel_name: channel.name
                            })
                            .then(response => {
                                callback(false, response.data);
                            })
                            .catch(error => {
                                callback(true, error);
                            });
                        }
                    };
                },
            });

            this.websocket.connector.pusher.connection.bind("state_change", function ({ previous, current }) {
                if (previous === Utils.PUSHER_STATE.CONNECTING) {
                    switch (current) {
                        case Utils.PUSHER_STATE.CONNECTED:
                            console.log("Websocket spojenie bolo úspešne nadviazané.");
                            break;
                        case Utils.PUSHER_STATE.DISCONNECTED:
                            console.error("Nepodarilo sa nadviazať WebSocket spojenie. Niektoré časti aplikácie nebudú fungovať správne.");
                            break;
                        case Utils.PUSHER_STATE.UNAVAILABLE:
                            console.error("WebSocket spojenie nie je dostupné. Niektoré časti aplikácie nebudú fungovať správne.");
                            break;
                        default:
                            break;
                    }
                } else if (previous === Utils.PUSHER_STATE.CONNECTED && current === Utils.PUSHER_STATE.CONNECTING) {
                    console.error("WebSocket spojenie bolo prerušené. Prebieha opätovné pripájanie.");
                } else if (previous === Utils.PUSHER_STATE.UNAVAILABLE && current === Utils.PUSHER_STATE.CONNECTED) {
                    console.log("WebSocket spojenie bolo opätovne nadviazané.");
                }
            });
            
            if (this.isPlayer) {
                const { lecturerId } = this.user;
                lecturerId && this.websocket.join(`Classroom.${lecturerId}`);
            }
            
            this.websocket.private(`Users.${this.user.id}`)
                .listen("NewGame", () => {
                    console.log("NewGame", Router.currentRoute.name);
                    if (Router.currentRoute.name === "dashboard") {
                        const dashboardStore = useDashboardStore();
                        
                        if (this.isPlayer) {
                            dashboardStore.loadLastInvitedGame();
                        } else {
                            dashboardStore.loadLastCreatedGame();
                        }
                    }
                });
        },

        setUserName(user) {
            if (!user) return;

            this.user.firstName = user['first_name'];
            this.user.lastName = user['last_name'];
            this.user.fullName = `${this.user.firstName} ${this.user.lastName}`;
        },

        updateProfilePhoto(avatar) {
            this.user.avatar = avatar;
        },

        pushNotification(notification) {
            this.notifications.push(notification);
        },

        removeNotification(index) {
            this.notifications.splice(index, 1);
        },

        clearNotifications() {
            this.notifications = [];
        },

        addErrors(errors) {
            Object.keys(errors).forEach(errorKey => {
                this.errors[errorKey] = errors[errorKey][0];
            });
        },

        clearErrors() {
            this.errors = {};
        },

        clearState() {
            const { websocket } = this;
            
            if (this.isPlayer) {
                const { lecturerId } = this.user;
                lecturerId && this.websocket.leave(`Classroom.${lecturerId}`);
            }
            
            websocket && websocket.disconnect();

            this.$reset();
        },
        
        async login(payload) {
            try {
                const response = await Http.post('/api/auth/login', payload);
                Storage.setAccessToken(response.data['access_token']);
                Storage.setRefreshToken(response.data['refresh_token']);

                Router.push('/dashboard', null);
            } catch (error) {
                if (!Utils.resolveValidationErrors(error)) {
                    this.addErrors({ login: [i18n.t('errorMessages.login.unknown')] });
                }
            }
        },
        
        async register(payload) {
            try {
                const response = await Http.post('/api/auth/register', payload);
                
                Storage.setAccessToken(response.data['access_token']);
                Storage.setRefreshToken(response.data['refresh_token']);
                
                Router.push('/dashboard', null);
                return true;
            } catch (error) {
                if (!Utils.resolveValidationErrors(error)) {
                    this.addErrors({ register: [i18n.t('errorMessages.register.unknown')] });
                }
                return false;
            }
        },

        logout() {            
            const dashboardStore = useDashboardStore();
            dashboardStore.clearState();
            
            const templatesStore = useTemplatesStore();
            templatesStore.clearState();
            
            const usersStore = useUsersStore();
            usersStore.clearState();
            
            const documentsStore = useDocumentsStore();
            documentsStore.clearState();
            
            const situationsStore = useSituationsStore();
            situationsStore.clearState();
            
            const gamesStore = useGamesStore();
            gamesStore.clearState();
            
            const gameStore = useGameStore();
            gameStore.clearState();
            
            Storage.removeAccessToken();
            Storage.removeRefreshToken();

            this.authenticate();
            this.clearState();
        },

        logoutAuth() {
            Http.get('/api/auth/logout')
                .catch(error => console.error(error))
                .finally(() => {
                    this.logout();
                    Router.push({ name: 'login' });
                });
        },

        authUser() {
            return new Promise((resolve, reject) => {
                Http.get('/api/auth/user')
                    .then(response => {
                        resolve(response.data);
                    })
                    .catch(error => reject(error));
            });
        },

        uploadProfilePhoto(payload) {
            return new Promise((resolve, reject) => {
                Http.post('/api/upload-photo', payload, {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    }
                })
                    .then(response => {
                        resolve(response.data);
                    })
                    .catch(error => reject(error));
            })
        },

        removeProfilePhoto() {
            Http.post('/api/remove-photo')
                .then(() => {
                    this.updateProfilePhoto(null);
                })
                .catch(error => console.error(error));
        },

        updateProfile(payload) {
            Http.put(`/api/update-profile`, payload)
                .then(() => {
                    const notification = Utils.createNotification(
                        'success',
                        i18n.t('notifications.profile_successfully_edited')
                    );

                    this.setUserName(payload)
                    this.clearNotifications();
                    this.pushNotification(notification);
                    Router.push('/dashboard', null);
                })

                .catch(error => Utils.resolveValidationErrors(error))
        },

        async getHelpText(role_id, name_of_page, language) {
            const { data } = await Http.post('/api/help/get-text', { role_id, name_of_page, language });
            return data;
        },

        async getPages(roleId) {
            const { data } = await Http.post('/api/help/get-pages', { role_id: roleId });
            return data;
        },

        editHelp(help) {
            return new Promise((resolve, reject) => {
                Http.post('/api/help/update', help)
                    .then(response => {
                        resolve(response);
                    })
                    .catch(error => {
                        reject(error);
                    });
            });
        },
    }
})